import type { LoginModalEventDetail } from '../../LoginModalManager';
import type { ArmSpec } from '../armRegistry';
import { hideEdgePill, showEdgePill } from '../edgePill/pillController';
import {
  clearDockState,
  readDockState,
  recordClaimed,
  saveDockState,
} from '../memory';

const DISMISSAL_DOCK_ARM = 'dismissal_dock';
const DOCK_MESSAGE = 'Your PDF is waiting';

function reopenWithDetail(detail: LoginModalEventDetail): void {
  document.dispatchEvent(new CustomEvent('open-login-modal', { detail }));
}

// Page-init rehydration: only dock state this arm stamped may resurrect the
// pill — a leftover blob from an ended override session or a bucket change
// must never render arm UI for a now-control visitor. No abTest() call here;
// the arm was validated at spawn time and the reopen re-resolves normally.
export function rehydrateDockPill(): void {
  const dock = readDockState();
  if (dock?.arm !== DISMISSAL_DOCK_ARM) return;
  showDockPill(dock.detail as unknown as LoginModalEventDetail);
}

// Shared by the fresh close (dismissalDockSpec.onClose) and the page-init
// rehydration, so both pills reopen through the identical wiring.
function showDockPill(detail: LoginModalEventDetail): void {
  showEdgePill({
    message: DOCK_MESSAGE,
    thumbnailUrl: detail.workThumbnailUrl,
    onClick: () => {
      clearDockState();
      hideEdgePill();
      reopenWithDetail(detail);
    },
    onDismiss: () => {
      clearDockState();
      hideEdgePill();
    },
  });
}

export const dismissalDockSpec: ArmSpec = {
  onClose: (ctx) => {
    try {
      saveDockState({
        arm: DISMISSAL_DOCK_ARM,
        detail: ctx.detail as unknown as Record<string, unknown>,
      });
      showDockPill(ctx.detail);
    } catch {
      /* a modal close must never fail; the dock is best-effort */
    }
  },
  // Navigation must never be lost: cleanup is synchronous, so try/finally is
  // the fail-safe shape (the redirect fires even if a memory write throws).
  wrapOnSuccess: (navigate) => (redirect) => {
    try {
      recordClaimed();
      clearDockState();
      hideEdgePill();
    } finally {
      navigate(redirect);
    }
  },
};
